import React, {  Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { FieldArray, FormikHandlers, FormikTouched, FormikErrors } from 'formik';

import { distances, colors, border } from '../../../../styles/constants';
import {
    DiscountSection,
    MarketingInput,
    TimePicker,
    MarketingDropdown,
    SmallMarketingButton,
    InputWithPostfixedText,
    DiscountTypeButton,
    EditWrapper
} from '../../../common/components';
import ItemsSelection from '../../containers/ItemsSelection';
import useHelpers from '../../../common/useHelpers';
import { Checkbox, ValidationState } from '../../../../components/Forms';
import { DiscountItem, Campaign, LoyaltyType, LoyaltyDiscountType, AssetRequestObject } from '../../../types';
import LoyaltyCard from './LoyaltyCard';
import { validateNested } from '../../helpers';
import { canReadProducts } from '../../../utils/accessUtils';
import { useAccount } from '../../../../settings/account/hooks';
import { getDefaultCountryCurrency } from '../../../../helpers/currency';
import Hr from '../../../../components/Hr';

interface LoyaltyDiscountProps {
    discount: DiscountItem;
    index: number;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    handleChange: FormikHandlers['handleChange'];
    handleBlur: FormikHandlers['handleBlur'];
    errors: FormikErrors<Campaign>;
    touched: FormikTouched<Campaign>;
    createAsset: (asset: AssetRequestObject) => void;
    removeAsset: (discountId: string) => void;
    isEditable?: boolean;
    isScheduledOrActive?: boolean;
    scopes: string[];
    asset?: string;
}

const LoyaltyDiscount = (
    {
        discount,
        index,
        setFieldValue,
        handleChange,
        handleBlur,
        errors,
        touched,
        createAsset,
        removeAsset,
        isEditable,
        isScheduledOrActive,
        scopes,
        asset,
    }: LoyaltyDiscountProps
) => {
    const { t } = useTranslation();
    const { days, months, itemsBought } = useHelpers();
    const { account } = useAccount();

    const [discountCombination, setDiscountCombination] = useState(discount.discountCombination === undefined || discount.discountCombination  === -1);

    const getRewardSymbol = (discountType?: LoyaltyDiscountType) => {
        if (discountType === LoyaltyDiscountType.inPercentage) return '%';
        if (discountType === LoyaltyDiscountType.inCurrency) return 'NOK';
        if (discountType === LoyaltyDiscountType.freeItem) return '';
    };

    const emptyRequirement = {
        timeFrom: new Date(),
        timeTo: new Date(),
        day: days[0].value,
    };

    const validate = validateNested(index, errors, touched);

    const validateRequirements = (requirementIndex: number, fieldName: string) => {
        if (touched.discounts && touched.discounts[index] && errors.discounts && errors.discounts[index]) {
            const fieldsErrors = errors.discounts[index] as any;
            const fieldsTouched = touched.discounts[index] as any;
            if (
                fieldsTouched.requirements &&
                fieldsTouched.requirements[requirementIndex] &&
                fieldsErrors.requirements &&
                fieldsErrors.requirements[requirementIndex]
            ) {
                const requirementsErrors = fieldsErrors.requirements[index] as any;
                const requirementsTouched = fieldsTouched.requirements[index] as any;
                return requirementsTouched[fieldName] && requirementsErrors[fieldName]
                    ? {
                        state: ValidationState.Invalid,
                        message: requirementsErrors[fieldName],
                    }
                    : undefined;
            }
            return undefined;
        }
    };

    return (
        <Fragment>
            <EditWrapper isEditable={!!isEditable}>
                {discount.loyaltyType === LoyaltyType.visitAndSpending && (
                    <Fragment>
                        <DiscountSection label={t('marketing.campaign.visits_spending')}>
                            <InputWrapper>
                                <MarketingInput
                                    type="number"
                                    label={t('marketing.campaign.how_many_visits')}
                                    labelOpacity={1}
                                    placeholder=""
                                    name={`discounts.${index}.visitsNumber`}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={discount.visitsNumber}
                                    validation={validate('visitsNumber')}
                                />
                            </InputWrapper>
                        </DiscountSection>
                        <DiscountSection label={t('marketing.campaign.requirements')}>
                            <Fragment>
                                <InputWrapper>
                                    <MarketingInput
                                        type="number"
                                        label={t('marketing.campaign.minimum_purchase')}
                                        labelOpacity={1}
                                        placeholder=""
                                        name={`discounts.${index}.minimumPurchase`}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={discount.minimumPurchase}
                                        validation={validate('minimumPurchase')}
                                    />
                                </InputWrapper>
                                <Hr />
                            </Fragment>
                            <FieldArray
                                name={`discounts.${index}.requirements`}
                                render={(arrayHelpers) => (
                                    <Fragment>
                                        {discount.requirements?.map((requirement, requirementIndex) => (
                                            <RequirementWrapper key={requirementIndex}>
                                                <RequirementInputsWrapper>
                                                    <Label>{t('marketing.campaign.visible_hours')}</Label>
                                                    <TimeRequirementWrapper>
                                                        <TimePickersWrapper>
                                                            <TimePicker
                                                                name={`discounts.${index}.requirements.${requirementIndex}.timeFrom`}
                                                                value={requirement.timeFrom}
                                                                setTime={(timeFrom) =>
                                                                    setFieldValue(
                                                                        `discounts.${index}.requirements.${requirementIndex}.timeFrom`,
                                                                        timeFrom
                                                                    )
                                                                }
                                                                isIndividual
                                                                validation={validateRequirements(
                                                                    requirementIndex,
                                                                    'timeFrom'
                                                                )}
                                                            />
                                                            <TimePicker
                                                                name={`discounts.${index}.requirements.${requirementIndex}.timeTo`}
                                                                value={requirement.timeTo}
                                                                setTime={(timeTo) =>
                                                                    setFieldValue(
                                                                        `discounts.${index}.requirements.${requirementIndex}.timeTo`,
                                                                        timeTo
                                                                    )
                                                                }
                                                                isIndividual
                                                            />
                                                        </TimePickersWrapper>
                                                        <DropdownWrapper>
                                                            <MarketingDropdown
                                                                name={`discounts.${index}.requirements.${requirementIndex}.day`}
                                                                value={
                                                                    days.filter(
                                                                        ({ value }) => value === requirement.day
                                                                    )[0]
                                                                }
                                                                onChange={(option) =>
                                                                    setFieldValue(
                                                                        `discounts.${index}.requirements.${requirementIndex}.day`,
                                                                        option.value
                                                                    )
                                                                }
                                                                options={days}
                                                            />
                                                        </DropdownWrapper>
                                                    </TimeRequirementWrapper>
                                                </RequirementInputsWrapper>
                                                <ActionButtonWrapper>
                                                    <ActionButton onClick={() => arrayHelpers.remove(requirementIndex)}>
                                                        {t('marketing.campaign.remove')}
                                                    </ActionButton>
                                                </ActionButtonWrapper>
                                            </RequirementWrapper>
                                        ))}
                                        <SmallMarketingButton
                                            text={t('marketing.campaign.add_requirements')}
                                            onClick={() => arrayHelpers.push(emptyRequirement)}
                                        />
                                    </Fragment>
                                )}
                            />
                        </DiscountSection>
                    </Fragment>
                )}
                {discount.loyaltyType === LoyaltyType.productsBought && canReadProducts(scopes) && (
                    <DiscountSection label={t('marketing.campaign.choose_items_stamps')}>
                        <ItemsSelection
                            name={`discounts.${index}.items`}
                            items={discount.items}
                            handleBlur={handleBlur}
                        />
                        <DropdownWrapper>
                            <MarketingDropdown
                                name={`discounts.${index}.itemsBought`}
                                label={t('marketing.campaign.items_bought')}
                                labelOpacity={1}
                                value={itemsBought.filter(({ value }) => value === discount.itemsBought)[0]}
                                onChange={(option) => setFieldValue(`discounts.${index}.itemsBought`, option.value)}
                                options={itemsBought}
                            />
                        </DropdownWrapper>
                    </DiscountSection>
                )}
                <DiscountSection label={t('marketing.campaign.select_reward')}>
                    <DiscountTypeButton
                        type="button"
                        onClick={() => {
                            setFieldValue(`discounts.${index}.loyaltyDiscountType`, LoyaltyDiscountType.inPercentage);
                        }}
                        isSelected={discount.loyaltyDiscountType === LoyaltyDiscountType.inPercentage}
                    >
                        {t('marketing.campaign.discount_percentage')}
                    </DiscountTypeButton>
                    <DiscountTypeButton
                        type="button"
                        onClick={() => {
                            setFieldValue(`discounts.${index}.loyaltyDiscountType`, LoyaltyDiscountType.inCurrency);
                        }}
                        isSelected={discount.loyaltyDiscountType === LoyaltyDiscountType.inCurrency}
                    >
                        {t('marketing.campaign.discount_currency', { currency: getDefaultCountryCurrency(account, ['NOK', 'SEK'], 'NOK') })}
                    </DiscountTypeButton>
                    {canReadProducts(scopes) && (
                        <DiscountTypeButton
                            type="button"
                            onClick={() => {
                                setFieldValue(`discounts.${index}.loyaltyDiscountType`, LoyaltyDiscountType.freeItem);
                            }}
                            isSelected={discount.loyaltyDiscountType === LoyaltyDiscountType.freeItem}
                        >
                            {t('marketing.campaign.free_item')}
                        </DiscountTypeButton>
                    )}
                </DiscountSection>
                {discount.loyaltyDiscountType === LoyaltyDiscountType.freeItem &&
                    discount.loyaltyType === LoyaltyType.visitAndSpending &&
                    canReadProducts(scopes) && (
                    <DiscountSection label={t('marketing.campaign.choose_items')}>
                        <ItemsSelection
                            name={`discounts.${index}.items`}
                            items={discount.items}
                            handleBlur={handleBlur}
                        />
                    </DiscountSection>
                )}
                <DiscountSection
                    label={`${t('marketing.campaign.discounts')} ${getRewardSymbol(discount.loyaltyDiscountType)}`}
                >
                    <DiscountValuesWrapper>
                        {discount.loyaltyDiscountType !== LoyaltyDiscountType.freeItem && (
                            <InputWrapper>
                                <InputWithPostfixedText
                                    type="number"
                                    label={t('marketing.campaign.reward')}
                                    labelOpacity={1}
                                    placeholder=""
                                    name={`discounts.${index}.discountValue`}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={discount.discountValue}
                                    validation={validate('discountValue')}
                                >
                                    {getRewardSymbol(discount.loyaltyDiscountType)}
                                </InputWithPostfixedText>
                            </InputWrapper>
                        )}
                        <DropdownWrapper>
                            <MarketingDropdown
                                name={`discounts.${index}.validFor`}
                                label={t('marketing.campaign.valid_for')}
                                labelOpacity={1}
                                value={months.filter(({ value }) => value === discount.validFor)[0]}
                                onChange={(option) => setFieldValue(`discounts.${index}.validFor`, option.value)}
                                options={months}
                            />
                        </DropdownWrapper>
                    </DiscountValuesWrapper>
                    <br />
                    <div>
                        {
                            <Checkbox name={`discounts.${index}.discount_combination`}
                                label={t('marketing.campaign.discount_combination')}
                                checked={discountCombination}
                                onChange={async () => {
                                    if (!discountCombination) {
                                        await setFieldValue(`discounts.${index}.discountCombination`, -1);
                                    } else {
                                        await setFieldValue(`discounts.${index}.discountCombination`, 0);
                                    }
                                    setDiscountCombination(!discountCombination);
                                }} />
                        }
                    </div>
                </DiscountSection>
            </EditWrapper>
            <EditWrapper isEditable={!!isScheduledOrActive}>
                <DiscountSection label={t('marketing.campaign.reward_detail')}>
                    <LoyaltyCard
                        discount={discount}
                        index={index}
                        createAsset={createAsset}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        removeAsset={removeAsset}
                        asset={asset}
                    />
                </DiscountSection>
            </EditWrapper>
        </Fragment>
    );
};

const RequirementWrapper = styled.div`
    display: flex;
    justify-content: space-between;

    @media (max-width: 768px) {
        display: block;
    }
`;

const RequirementInputsWrapper = styled.div`
    max-width: 90%;

    @media (max-width: 768px) {
        max-width: 100%;
    }
`;

const InputWrapper = styled.div`
    width: 25vw;

    @media (max-width: 768px) {
        display: block;
        width: 100%;
    }
`;

const ActionButtonWrapper = styled.div`
    margin-top: ${distances.small};

    @media (max-width: 768px) {
        margin-top: 0;
        margin-bottom: ${distances.small};
        text-align: right;
    }
`;

const ActionButton = styled.button`
    border: none;
    background: none;
    cursor: pointer;
    opacity: 0.5;
    padding: 0;
    white-space: nowrap;

    &:focus {
        outline: none;
    }
`;



const Label = styled.label`
    padding: ${distances.tiny} 0;
    font-weight: 700;
    display: block;
    opacity: 0.5;
    white-space: nowrap;
`;

const TimeRequirementWrapper = styled.div`
    display: flex;
    margin-bottom: ${distances.small};

    @media (max-width: 768px) {
        display: block;
    }
`;

const TimePickersWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 25vw;
    margin-right: ${distances.small};

    > *:first-child {
        width: 45%;
    }

    > *:nth-child(2) {
        width: 45%;
    }

    @media (max-width: 768px) {
        width: 100%;
        margin-bottom: ${distances.small};
    }
`;

const DiscountValuesWrapper = styled.div`
    display: flex;

    > *:first-child {
        margin-right: ${distances.small};
    }
`;

const DropdownWrapper = styled.div`
    width: 25vw;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

export default LoyaltyDiscount;
